// src/pages/Profile.tsx
import React, { useState, useEffect, useCallback } from 'react';
import { Card, Descriptions, Button, Form, Input, message, Divider, Modal, Upload, Space, Select } from 'antd';
import { useGetIdentity, useLogout } from '@refinedev/core';
import { UserOutlined, MailOutlined, CalendarOutlined, LockOutlined, UploadOutlined, GlobalOutlined } from '@ant-design/icons';
import { updatePassword, EmailAuthProvider, reauthenticateWithCredential, deleteUser } from 'firebase/auth';
import { auth, db, storage } from '../utils/firebase';
import { doc, deleteDoc, updateDoc, getDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';

const Profile: React.FC = () => {
  const { data: user, refetch } = useGetIdentity<any>();
  const { mutate: logout } = useLogout();
  const [loading, setLoading] = useState(false);
  const [passwordModalVisible, setPasswordModalVisible] = useState(false);
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [form] = Form.useForm();
  const [passwordForm] = Form.useForm();
  const [deleteForm] = Form.useForm();

  // Logo upload states
  const [logoUrl, setLogoUrl] = useState<string>('');
  const [uploading, setUploading] = useState(false);
  const [fileList, setFileList] = useState<any[]>([]);

  // User data state
  const [userData, setUserData] = useState<any>({});

  // Form change detection
  const [initialValues, setInitialValues] = useState<any>({});
  const [hasChanges, setHasChanges] = useState(false);

  // Fetch user data including logo
  const fetchUserData = useCallback(async () => {
    if (!auth.currentUser) return;

    try {
      const userDoc = await getDoc(doc(db, 'users', auth.currentUser.uid));
      if (userDoc.exists()) {
        const data = userDoc.data();

        // Store user data in state
        setUserData(data);

        if (data.companyLogo) {
          setLogoUrl(data.companyLogo);
          setFileList([{
            uid: '-1',
            name: 'company-logo',
            status: 'done',
            url: data.companyLogo,
          }]);
        }

        // Prepare form values
        const formValues = {
          name: data.name || user?.name || '',
          email: data.email || user?.email || '',
          phone: data.phone || '',
          company: data.company || '',
          companyRole: data.companyRole || '',
          defaultAIInstructions: data.defaultAIInstructions || '',
          language: data.language || 'en',
        };

        // Update form with additional fields
        form.setFieldsValue(formValues);

        // Store initial values for change detection
        setInitialValues(formValues);
        setHasChanges(false);
      }
    } catch (error) {
      console.error('Error fetching user data:', error);
    }
  }, [user, form]);

  useEffect(() => {
    fetchUserData();
  }, [fetchUserData]);

  const handleLogoUpload = async (file: any) => {
    if (!auth.currentUser) return false;

    // Validate file type
    const isImage = file.type.startsWith('image/');
    if (!isImage) {
      message.error('You can only upload image files!');
      return false;
    }

    // Validate file size (max 5MB)
    const isLt5M = file.size / 1024 / 1024 < 5;
    if (!isLt5M) {
      message.error('Image must be smaller than 5MB!');
      return false;
    }

    setUploading(true);
    try {
      // Create a reference to the logo in Firebase Storage
      const timestamp = Date.now();
      const fileName = `logos/${auth.currentUser.uid}/company-logo-${timestamp}`;
      const storageRef = ref(storage, fileName);

      // Upload the file
      const snapshot = await uploadBytes(storageRef, file);
      
      // Get the download URL
      const downloadURL = await getDownloadURL(snapshot.ref);
      
      // Update user document with logo URL
      await updateDoc(doc(db, 'users', auth.currentUser.uid), {
        companyLogo: downloadURL,
        updatedAt: new Date()
      });

      setLogoUrl(downloadURL);
      setFileList([{
        uid: '-1',
        name: file.name,
        status: 'done',
        url: downloadURL,
      }]);
      
      message.success('Company logo uploaded successfully!');
    } catch (error) {
      console.error('Error uploading logo:', error);
      message.error('Failed to upload logo');
    } finally {
      setUploading(false);
    }

    return false; // Prevent default upload behavior
  };

  const handleRemoveLogo = async () => {
    if (!auth.currentUser) return;

    try {
      await updateDoc(doc(db, 'users', auth.currentUser.uid), {
        companyLogo: null,
        updatedAt: new Date()
      });
      
      setLogoUrl('');
      setFileList([]);
      message.success('Company logo removed');
    } catch (error) {
      console.error('Error removing logo:', error);
      message.error('Failed to remove logo');
    }
  };

  const handleSave = async (values: any) => {
    setLoading(true);
    try {
      if (!auth.currentUser) throw new Error('No authenticated user');

      // Update user document in Firestore
      await updateDoc(doc(db, 'users', auth.currentUser.uid), {
        name: values.name,
        phone: values.phone,
        company: values.company,
        companyRole: values.companyRole,
        defaultAIInstructions: values.defaultAIInstructions || '',
        language: values.language || 'en',
        updatedAt: new Date()
      });

      // Update local state
      setUserData({
        ...userData,
        name: values.name,
        phone: values.phone,
        company: values.company,
        companyRole: values.companyRole,
        defaultAIInstructions: values.defaultAIInstructions || '',
        language: values.language || 'en',
      });

      message.success('Profile updated successfully!');
      refetch(); // Refresh user data
      fetchUserData(); // Refresh form data and reset initial values
    } catch (error) {
      console.error('Error updating profile:', error);
      message.error('Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  // Handle form value changes to detect if form has been modified
  const handleValuesChange = () => {
    const currentValues = form.getFieldsValue();
    const changed = Object.keys(initialValues).some(key => {
      return currentValues[key] !== initialValues[key];
    });
    setHasChanges(changed);
  };

  const handlePasswordChange = async (values: any) => {
    setLoading(true);
    try {
      const currentUser = auth.currentUser;
      if (!currentUser || !currentUser.email) {
        throw new Error('No authenticated user found');
      }

      // Re-authenticate user first
      const credential = EmailAuthProvider.credential(
        currentUser.email,
        values.currentPassword
      );
      await reauthenticateWithCredential(currentUser, credential);

      // Update password
      await updatePassword(currentUser, values.newPassword);
      
      message.success('Password updated successfully!');
      setPasswordModalVisible(false);
      passwordForm.resetFields();
    } catch (error: any) {
      console.error('Error updating password:', error);
      if (error.code === 'auth/wrong-password') {
        message.error('Current password is incorrect');
      } else if (error.code === 'auth/weak-password') {
        message.error('New password is too weak');
      } else {
        message.error('Failed to update password');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAccount = async (values: any) => {
    setLoading(true);
    try {
      const currentUser = auth.currentUser;
      if (!currentUser || !currentUser.email) {
        throw new Error('No authenticated user found');
      }

      // Re-authenticate user first
      const credential = EmailAuthProvider.credential(
        currentUser.email,
        values.password
      );
      await reauthenticateWithCredential(currentUser, credential);

      // Delete user data from Firestore
      await deleteDoc(doc(db, 'users', currentUser.uid));

      // Delete the user account
      await deleteUser(currentUser);
      
      message.success('Account deleted successfully');
      setDeleteModalVisible(false);
      
      // Force logout and redirect to login
      setTimeout(() => {
        logout();
      }, 1000);
      
    } catch (error: any) {
      console.error('Error deleting account:', error);
      if (error.code === 'auth/wrong-password') {
        message.error('Password is incorrect');
      } else {
        message.error('Failed to delete account. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  if (!user) {
    return <div>Loading...</div>;
  }

  return (
    <div className="max-w-4xl mx-auto p-6">
      <Card
        title={
          <div className="flex items-center gap-2">
            <UserOutlined />
            <span>Account Settings</span>
          </div>
        }
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSave}
          onValuesChange={handleValuesChange}
          autoComplete="off"
        >
            <Form.Item
              label="Full Name"
              name="name"
              rules={[{ required: true, message: 'Please input your name!' }]}
            >
              <Input 
                prefix={<UserOutlined />} 
                size="large" 
                placeholder="Enter your full name"
              />
            </Form.Item>
            
            <Form.Item
              label="Email"
              name="email"
              help="Email cannot be changed. Contact support if needed."
              rules={[
                { required: true, message: 'Please input your email!' },
                { type: 'email', message: 'Please enter a valid email!' }
              ]}
            >
              <Input 
                prefix={<MailOutlined />} 
                size="large" 
                disabled
              />
            </Form.Item>

            <Form.Item
              label="Phone"
              name="phone"
            >
              <Input 
                size="large" 
                placeholder="+1 (555) 123-4567"
              />
            </Form.Item>

            <Form.Item
              label="Company"
              name="company"
            >
              <Input 
                size="large" 
                placeholder="Enter your company name"
              />
            </Form.Item>

            <Form.Item
              label="Role"
              name="companyRole"
            >
              <Input
                size="large"
                placeholder="e.g., Home Inspector"
              />
            </Form.Item>

            <Form.Item
              label="Preferred Language"
              name="language"
              help="Language for generated scripts and narration"
            >
              <Select
                size="large"
                placeholder="Select language"
              >
                <Select.Option value="en">
                  <Space>
                    <GlobalOutlined />
                    <span>English</span>
                  </Space>
                </Select.Option>
                <Select.Option value="es">
                  <Space>
                    <GlobalOutlined />
                    <span>Spanish</span>
                  </Space>
                </Select.Option>
              </Select>
            </Form.Item>

            <Form.Item
              label="Default AI Instructions"
              name="defaultAIInstructions"
              help={`${form.getFieldValue('defaultAIInstructions')?.length || 0}/400 characters`}
              rules={[
                { max: 400, message: 'Maximum 400 characters allowed' }
              ]}
            >
              <Input.TextArea
                size="large"
                rows={3}
                placeholder="e.g., Default prompt additons -- always use simple language."
                maxLength={400}
                showCount
              />
            </Form.Item>
            <br/>
            <Form.Item label="Company Logo">
              <Space direction="vertical" style={{ width: '100%' }}>
                {logoUrl && (
                  <div style={{ marginBottom: 16 }}>
                    <div style={{ 
                      width: 200, 
                      height: 100, 
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      backgroundColor: '#f5f5f5',
                      borderRadius: 8,
                      overflow: 'hidden',
                      border: '1px solid #e0e0e0'
                    }}>
                      <img 
                        src={logoUrl} 
                        alt="Company Logo"
                        style={{ 
                          maxWidth: '100%', 
                          maxHeight: '100%',
                          objectFit: 'contain'
                        }}
                      />
                    </div>
                  </div>
                )}
                
                <Space>
                  <Upload
                    name="logo"
                    listType="picture"
                    fileList={fileList}
                    beforeUpload={handleLogoUpload}
                    onRemove={handleRemoveLogo}
                    maxCount={1}
                    showUploadList={false}
                  >
                    <Button icon={<UploadOutlined />} loading={uploading}>
                      {logoUrl ? 'Change Logo' : 'Upload Company Logo'}
                    </Button>
                  </Upload>
                  
                  {logoUrl && (
                    <Button 
                      danger 
                      onClick={handleRemoveLogo}
                    >
                      Remove Logo
                    </Button>
                  )}
                </Space>
                
                <div style={{ color: '#888', fontSize: '12px' }}>
                  Recommended: Square image, at least 300x300px, max 5MB
                  <br />
                  Rectangular logos are also supported
                </div>
              </Space>
            </Form.Item>
            
          <Form.Item>
            <Button
              type="primary"
              htmlType="submit"
              loading={loading}
              disabled={!hasChanges}
            >
              Save Changes
            </Button>
          </Form.Item>
        </Form>
      </Card>

      {/* Account Statistics */}
      <Card title="Account Statistics" className="mt-6">
        <Descriptions bordered>
          <Descriptions.Item label="Videos This Month">
            {user.usage?.videosThisMonth || 0}
          </Descriptions.Item>
          <Descriptions.Item label="Scripts This Month">
            {user.usage?.scriptsThisMonth || 0}
          </Descriptions.Item>
          <Descriptions.Item label="Total Videos Generated">
            {user.usage?.videosAllTime || 0}
          </Descriptions.Item>
          <Descriptions.Item label="Total Scripts Generated">
            {user.usage?.scriptsAllTime || 0}
          </Descriptions.Item>
          <Descriptions.Item label="Account Created" span={2}>
            <div className="flex items-center gap-1">
              <CalendarOutlined />
              <span>{new Date().toLocaleDateString()}</span>
            </div>
          </Descriptions.Item>
        </Descriptions>
      </Card>

      {/* Account Actions */}
      <Card title="Account Actions" className="mt-6">
        <div className="space-y-4">
          <div className="flex justify-between items-center p-4 border rounded">
            <div>
              <h4 className="font-medium">Change Password</h4>
              <p className="text-gray-600 text-sm">Update your account password</p>
            </div>
            <Button onClick={() => setPasswordModalVisible(true)}>
              Change Password
            </Button>
          </div>
          
          <Divider />
          
          <div className="flex justify-between items-center p-4 border border-red-200 rounded bg-red-50">
            <div>
              <h4 className="font-medium text-red-800">Delete Account</h4>
              <p className="text-red-600 text-sm">Permanently delete your account and all data</p>
            </div>
            <Button danger onClick={() => setDeleteModalVisible(true)}>
              Delete Account
            </Button>
          </div>
        </div>
      </Card>

      {/* Change Password Modal */}
      <Modal
        title="Change Password"
        open={passwordModalVisible}
        onCancel={() => {
          setPasswordModalVisible(false);
          passwordForm.resetFields();
        }}
        footer={null}
      >
        <Form
          form={passwordForm}
          layout="vertical"
          onFinish={handlePasswordChange}
        >
          <Form.Item
            name="currentPassword"
            label="Current Password"
            rules={[{ required: true, message: 'Please enter your current password!' }]}
          >
            <Input.Password 
              prefix={<LockOutlined />} 
              placeholder="Enter current password"
            />
          </Form.Item>

          <Form.Item
            name="newPassword"
            label="New Password"
            rules={[
              { required: true, message: 'Please enter a new password!' },
              { min: 6, message: 'Password must be at least 6 characters!' }
            ]}
          >
            <Input.Password 
              prefix={<LockOutlined />} 
              placeholder="Enter new password"
            />
          </Form.Item>

          <Form.Item
            name="confirmPassword"
            label="Confirm New Password"
            dependencies={['newPassword']}
            rules={[
              { required: true, message: 'Please confirm your password!' },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue('newPassword') === value) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error('Passwords do not match!'));
                },
              }),
            ]}
          >
            <Input.Password 
              prefix={<LockOutlined />} 
              placeholder="Confirm new password"
            />
          </Form.Item>

          <Form.Item>
            <div className="flex gap-2">
              <Button type="primary" htmlType="submit" loading={loading}>
                Update Password
              </Button>
              <Button onClick={() => {
                setPasswordModalVisible(false);
                passwordForm.resetFields();
              }}>
                Cancel
              </Button>
            </div>
          </Form.Item>
        </Form>
      </Modal>

      <Divider />

      {/* Delete Account Modal */}
      <Modal
        title="Delete Account"
        open={deleteModalVisible}
        onCancel={() => {
          setDeleteModalVisible(false);
          deleteForm.resetFields();
        }}
        footer={null}
      >
        <div className="mb-4">
          <p className="text-red-600 font-medium">Warning: This action cannot be undone!</p>
          <p className="text-gray-600 mt-2">
            Deleting your account will permanently remove:
          </p>
          <ul className="list-disc list-inside text-gray-600 mt-2 ml-4">
            <li>Your profile and account information</li>
            <li>All generated videos and scripts</li>
            <li>Your subscription and billing history</li>
          </ul>
        </div>

        <Form
          form={deleteForm}
          layout="vertical"
          onFinish={handleDeleteAccount}
        >
          <Form.Item
            name="confirmation"
            label="Type 'DELETE' to confirm"
            rules={[
              { required: true, message: 'Please type DELETE to confirm!' },
              { 
                validator: (_, value) => {
                  if (value === 'DELETE') {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error('Please type DELETE exactly'));
                }
              }
            ]}
          >
            <Input placeholder="Type DELETE" />
          </Form.Item>

          <Form.Item
            name="password"
            label="Enter your password to confirm"
            rules={[{ required: true, message: 'Please enter your password!' }]}
          >
            <Input.Password 
              prefix={<LockOutlined />} 
              placeholder="Enter your password"
            />
          </Form.Item>

          <Form.Item>
            <div className="flex gap-2">
              <Button type="primary" danger htmlType="submit" loading={loading}>
                Delete My Account
              </Button>
              <Button onClick={() => {
                setDeleteModalVisible(false);
                deleteForm.resetFields();
              }}>
                Cancel
              </Button>
            </div>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default Profile;